import { colours } from "@shared/ui.design-system";
import { Typography } from "@shared/ui.primitives";
import React from "react";

import { baseContainer, baseStar, reviewText, star } from "./Rating.css";

type BaseProps = {
  numberOfStars?: number;
  totalReviews?: number;
  ratingValue: number;
  activeColor?: string;
  inactiveColor?: string;
  includeCountSuffix?: boolean;
  size?: "sm" | "lg";
};

type PropsWithLink = BaseProps & {
  renderCountAsLink?: true;
  trackReviewsClick: () => void;
};

type PropsWithoutLink = BaseProps & {
  renderCountAsLink?: false;
  trackReviewsClick?: never;
};

export type Props = PropsWithLink | PropsWithoutLink;

const STAR_PIXELS: Record<NonNullable<BaseProps["size"]>, number> = {
  sm: 12,
  lg: 16
};

export const getBackgroundImage = (color: string): string => {
  const svg = encodeURIComponent(
    `<svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path fill-rule="evenodd" clip-rule="evenodd"
        d="M10 13.4845L4.85688 17.0789L6.686 11.0768L1.67826 7.2961L7.95183 7.18094L10 1.25L12.0482 7.18094L18.3217 7.2961L13.314 11.0768L15.1431 17.0789L10 13.4845Z"
        fill="${color}"/>
    </svg>`
  );
  return `url('data:image/svg+xml;utf8,${svg}')`;
};

export const maxRatingValue = 5;

export const Rating: React.FC<Props> = ({
  ratingValue,
  numberOfStars = 5,
  inactiveColor = colours.secondary.dark,
  activeColor = colours.primary.dark,
  totalReviews = 0,
  trackReviewsClick,
  renderCountAsLink = false,
  includeCountSuffix = false,
  size = "lg"
}) => {
  const starPx = STAR_PIXELS[size];
  const baseWidth = starPx * numberOfStars;
  const topWidth = (ratingValue / maxRatingValue) * baseWidth;

  const reviewsText = totalReviews
    ? includeCountSuffix
      ? `${totalReviews} reviews`
      : `${totalReviews}`
    : "";

  const BaseRating = () => {
    const commonBg = {
      backgroundRepeat: "repeat-x" as const,
      backgroundSize: `${starPx}px ${starPx}px`,
      height: `${starPx}px`
    };

    return (
      <>
        <div
          className={baseStar}
          style={{
            width: `${baseWidth}px`,
            backgroundImage: getBackgroundImage(inactiveColor),
            ...commonBg
          }}
          aria-hidden
        />
        <div
          className={star}
          style={{
            width: `${topWidth}px`,
            backgroundImage: getBackgroundImage(activeColor),
            ...commonBg
          }}
          aria-hidden
        />
        <div className={reviewText}>
          <Typography colour="primary-dark" variant="smallprint-md-bold">
            {ratingValue.toFixed(1) ?? ""}
          </Typography>
          {reviewsText && (
            <>
              <Typography
                as="span"
                variant="smallprint-md"
                colour="primary-light"
              >
                {"("}
              </Typography>
              <Typography
                as="span"
                variant="smallprint-md"
                underline={renderCountAsLink}
                colour="primary-light"
              >
                {reviewsText}
              </Typography>
              <Typography
                as="span"
                variant="smallprint-md"
                colour="primary-light"
              >
                {")"}
              </Typography>
            </>
          )}
        </div>
      </>
    );
  };

  const Wrapper = renderCountAsLink ? "a" : "div";
  const wrapperProps = renderCountAsLink
    ? { href: "#reviews", onClick: trackReviewsClick }
    : {};

  return (
    <Wrapper
      {...wrapperProps}
      className={baseContainer}
      aria-label={`Rating ${ratingValue.toFixed(1)} out of ${maxRatingValue}${
        totalReviews ? ` from ${totalReviews} reviews` : ""
      }`}
      role="img"
    >
      <BaseRating />
    </Wrapper>
  );
};
